import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSession } from './SessionManager';
import { claimAnonymousUserData, resolveAnonymousClaimSource } from './anonymousClaim';
import { usePlayerPersistenceEnabled } from './PlayerPersistenceContext';

/**
 * React effect component that watches for anonymous → authenticated user transitions
 * and claims anonymous data when a user signs in.
 */
export function AnonymousClaimer(): null {
  const { user, requestsReady } = useSession();
  const queryClient = useQueryClient();
  const playerPersistenceEnabled = usePlayerPersistenceEnabled();
  const previousUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    const currentUserId = user?.id || null;

    if (!playerPersistenceEnabled || requestsReady === false) return;

    // Claim on null → user transition or on mount if user exists
    if (currentUserId && !previousUserId) {
      const anonymousId = resolveAnonymousClaimSource();
      if (anonymousId) {
        void claimAnonymousUserData(anonymousId, queryClient, {
          expectedAuthenticatedUserId: currentUserId,
        });
      }
    }

    previousUserIdRef.current = currentUserId;
  }, [playerPersistenceEnabled, requestsReady, user, queryClient]);

  return null;
}
